import { useQuery } from "@apollo/client/react";
import { Route, Routes, useParams, useResolvedPath } from "react-router";

import { getBreadcrumbsBackUrl } from "components/Breadcrumbs/helpers";
import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import useErrorHandle from "hooks/useErrorHandle";
import useCanUpdateContext from "shared/Context/access/useCanUpdateContext";
import { Context as ContextType } from "types/generated";

import { FILTERS_ORDER_SETTINGS_KEY } from "../Contexts/constants";
import { ContextContext } from "./Context";
import ContextHeader from "./Header";
import ContextHooks from "./Hooks";
import ContextMountedFiles from "./MountedFiles";
import ContextUsedBy from "./UsedBy";
import ContextVariables from "./Variables";
import { GET_CONTEXT } from "./gql";

const Context = () => {
  const resolvedPath = useResolvedPath("..");
  const { contextId } = useParams<{ contextId: string }>();

  const { error, loading, data } = useQuery<{ context: ContextType }>(GET_CONTEXT, {
    variables: { contextId },
  });

  useBreadcrumbs(
    [
      {
        title: "Contexts",
        link: getBreadcrumbsBackUrl("/contexts", FILTERS_ORDER_SETTINGS_KEY),
      },
      {
        title: data?.context?.name || "",
      },
    ],
    [data?.context?.name]
  );

  const canUpdateContextFn = useCanUpdateContext();

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data?.context) {
    return <PageLoading />;
  }

  if (!data?.context) {
    return <NotFoundPage />;
  }

  const canUpdateContext = canUpdateContextFn(data.context);

  const contextData = {
    context: data.context,
    contextUrl: resolvedPath.pathname,
    canUpdateContext,
    isContextLoading: loading,
  };

  return (
    <ContextContext.Provider value={contextData}>
      <ContextHeader />
      <Routes>
        <Route index element={<ContextVariables />} />

        <Route path="files" element={<ContextMountedFiles />} />

        <Route path="used-by" element={<ContextUsedBy />} />

        <Route path="hooks" element={<ContextHooks />} />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </ContextContext.Provider>
  );
};

export default Context;
